/* Shared sub-tab → URL sync hook.
   Keeps a `view` useState in lock-step with React Router's :sub path segment
   so any nested Day-Trading sub-tab (Options sub-views, Scanner live/history,
   Journal sub-views, Vault sub-views) becomes deep-linkable.

   Usage:
     const [view, setView] = useSubTab("planner", {
       slugToKey: { planner: "planner", log: "log", ... },
       keyToSlug: { planner: "planner", log: "log", ... },
       parentSlug: "options",
     });

   When the user clicks a sub-view, `setView(newKey)` updates state AND pushes
   `/day-trading/<parentSlug>/<keyToSlug[newKey]>` onto the history stack.
   When the user navigates back/forward, useParams updates and the hook
   mirrors that change into state. */
import { useEffect, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

export function useSubTab(defaultKey, opts) {
  const { slugToKey, keyToSlug, parentSlug } = opts;
  const params = useParams();
  const navigate = useNavigate();

  // Initial key from URL (if any), falling back to default.
  const initial = params?.sub && slugToKey[params.sub] ? slugToKey[params.sub] : defaultKey;
  const [view, setViewState] = useState(initial);

  // Mirror URL → state on browser nav.
  useEffect(() => {
    if (params?.sub && slugToKey[params.sub] && slugToKey[params.sub] !== view) {
      setViewState(slugToKey[params.sub]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.sub]);

  // Wrap setState so a click pushes the URL too.
  const setView = useCallback((k) => {
    setViewState(k);
    const slug = keyToSlug[k];
    if (slug) {
      const target = `/day-trading/${parentSlug}/${slug}`;
      if (window.location.pathname !== target) {
        navigate(target, { replace: false });
      }
    }
  }, [keyToSlug, parentSlug, navigate]);

  return [view, setView];
}
